import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { WalletError } from '@cef-ai/wallet-api-client';
import { useWallet } from '~/wallet';
import { AppShell, Button, Card, ErrorText, Spinner } from '~/ui/primitives';
import { EmailStep } from './EmailStep';
import { OtpStep } from './OtpStep';
import { registerErrorMessage } from './registerErrorMessage';

/**
 * `email` → `otp` → `passkey`, plus the `already-registered` branch off step 2.
 *
 * The order is chosen for the enumeration boundary, not for feel:
 * `email/start` answers identically for a bound and an unbound address, so it
 * discloses nothing; only `email/verify`, to a caller who has just proven
 * mailbox control, may say `already-registered`. Verifying before the ceremony
 * also means abandoning halfway leaves nothing behind but an expired
 * verification row — no half-created account, no orphan credential.
 *
 * State is local `useState`, matching what this route already did. There is no
 * store here on purpose: nothing outside this component observes the step, and
 * the machine dies with the route.
 */
type Step = 'email' | 'otp' | 'passkey' | 'already-registered';

export const Register = observer(() => {
  const { identity, apiClient } = useWallet();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [emailToken, setEmailToken] = useState<string | null>(null);
  /**
   * The address `email/verify` actually attested, captured at that moment
   * rather than re-read from the `email` box later. It is what the passkey
   * step displays as confirmed and what goes into the WebAuthn `user.name`
   * hint, so the two can never describe different addresses — including if
   * some future edit lets the box be changed after verification.
   */
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  /**
   * The live OTP lifetime, straight off `email/start`. Captured rather than
   * discarded so step 2 can say how long the code actually lasts: the TTL is
   * a wallet-api deployment config value, so this response is the only thing
   * in the browser that knows it. `null` until a send succeeds, and reset on
   * every send so a resend under a changed config does not leave the previous
   * duration on screen.
   */
  const [expiresInSeconds, setExpiresInSeconds] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  /**
   * Spec §8 Q3, settled: default the display name from the email's local part
   * rather than making it a required field — one fewer box on the flow we are
   * trying to smooth, and still editable above.
   */
  function displayName(): string {
    return name.trim() || email.trim().split('@')[0];
  }

  async function sendCode() {
    setError(null);
    setBusy(true);
    try {
      const { expiresInSeconds: ttl } = await apiClient.accounts.emailStart(email.trim());
      setExpiresInSeconds(ttl ?? null);
      setCode('');
      setStep('otp');
    } catch (e) {
      setError(registerErrorMessage(e, 'email'));
    } finally {
      setBusy(false);
    }
  }

  async function resendCode() {
    setError(null);
    setResent(false);
    setResending(true);
    try {
      const { expiresInSeconds: ttl } = await apiClient.accounts.emailStart(email.trim());
      setExpiresInSeconds(ttl ?? null);
      setResent(true);
    } catch (e) {
      setError(registerErrorMessage(e, 'email'));
    } finally {
      setResending(false);
    }
  }

  async function verifyCode() {
    setError(null);
    // Clear the resend acknowledgement too: after a resend followed by a failed
    // verify, "Code sent again" would otherwise still be sitting on the button
    // describing an action two steps in the past.
    setResent(false);
    setBusy(true);
    let verified: { emailToken: string; status: 'verified' | 'already-registered' };
    try {
      verified = await apiClient.accounts.emailVerify(email.trim(), code.trim());
    } catch (e) {
      setError(registerErrorMessage(e, 'otp'));
      setBusy(false);
      return;
    }
    setEmailToken(verified.emailToken);
    setVerifiedEmail(email.trim());
    if (verified.status === 'already-registered') {
      // The address belongs to an existing account. Registering again would
      // create a SECOND wallet at a different address — offer sign-in instead.
      setStep('already-registered');
      setBusy(false);
      return;
    }
    setStep('passkey');
    // Chained off the same click deliberately: WebAuthn `create()` needs user
    // activation, and one awaited request is well inside the activation
    // window. If it is lost anyway, `runCeremony` leaves the passkey step
    // showing a "Create passkey" button, which is a fresh, real click.
    await runCeremony(verified.emailToken, email.trim());
  }

  async function runCeremony(token: string, address: string) {
    setError(null);
    setBusy(true);
    try {
      // `email` becomes the WebAuthn `user.name` — the line Apple Passwords and
      // its equivalents show as the account. Without it the entry reads
      // `scp-wallet` and identifies nothing (CEF-AI/wallet#72).
      await identity.register({ emailToken: token, displayName: displayName(), email: address });
      navigate('/account');
    } catch (e) {
      // The emailToken is single-use. `passkey/register/start` may already have
      // consumed it by the time the ceremony fails, in which case retrying with
      // the SAME token can only ever fail again — a retry button that exists
      // precisely for the failure it cannot recover. We do not depend on
      // knowing the server's consumption point: whenever a post-verification
      // step reports `email-unverified`, we treat the token as spent, drop it,
      // and send the user back to the address step for a fresh code. Anything
      // else (a cancelled prompt, a PRF-less authenticator, a network blip)
      // leaves the token good and keeps the retry.
      if (e instanceof WalletError && e.code === 'email-unverified') {
        setEmailToken(null);
        setVerifiedEmail(null);
        setCode('');
        setResent(false);
        setStep('email');
      }
      setError(registerErrorMessage(e, 'passkey'));
      setBusy(false);
    }
  }

  return (
    <AppShell footer="Your wallet is created on this device and secured by its passkey.">
      <Card>
        {step === 'email' && (
          <EmailStep
            name={name}
            email={email}
            onNameChange={setName}
            onEmailChange={setEmail}
            onSubmit={sendCode}
            busy={busy}
            error={error}
          />
        )}
        {step === 'otp' && (
          <OtpStep
            email={email.trim()}
            expiresInSeconds={expiresInSeconds}
            code={code}
            onCodeChange={setCode}
            onVerify={verifyCode}
            onResend={resendCode}
            onBack={() => {
              setError(null);
              setResent(false);
              setStep('email');
            }}
            busy={busy}
            resending={resending}
            resent={resent}
            error={error}
          />
        )}
        {step === 'passkey' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <h1 style={{ margin: 0, font: '600 24px/1.25 var(--font-display)', letterSpacing: '-0.01em' }}>
                Create your passkey
              </h1>
              <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                {email.trim()} is confirmed. Your device will now create the passkey that secures this wallet.
              </p>
            </div>
            {busy ? (
              <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                <Spinner /> Waiting for your device…
              </p>
            ) : (
              // Retry only re-runs the ceremony: the code is spent, and the
              // token we already hold is what proves the mailbox. This button
              // is only ever reachable while that token is still good — a
              // failure that reports `email-unverified` (a spent token) takes
              // the user back to the address step instead of landing here.
              <Button
                onClick={() => emailToken && verifiedEmail && runCeremony(emailToken, verifiedEmail)}
                disabled={!emailToken || !verifiedEmail}
              >
                Create passkey
              </Button>
            )}
            {error && <ErrorText>{error}</ErrorText>}
          </div>
        )}
        {step === 'already-registered' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <h1 style={{ margin: 0, font: '600 24px/1.25 var(--font-display)', letterSpacing: '-0.01em' }}>
                You already have a wallet
              </h1>
              <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                {email.trim()} is already registered. Sign in with its passkey — creating another wallet would give you
                a second, separate set of addresses.
              </p>
            </div>
            <Button onClick={() => navigate('/login')}>Sign in</Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => {
                setError(null);
                setCode('');
                setEmailToken(null);
                setVerifiedEmail(null);
                setStep('email');
              }}
            >
              Use a different address
            </Button>
          </div>
        )}
      </Card>
      {step === 'email' && (
        <p style={{ margin: 0, textAlign: 'center', color: 'var(--text-secondary)' }}>
          Already have a wallet? <Link to="/login">Sign in</Link>
        </p>
      )}
    </AppShell>
  );
});
